import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTour, TOUR_STEPS } from "./TourContext";
import { ArrowLeft, ArrowRight, X, Compass } from "lucide-react";
import { Button } from "./ui/button";

const PADDING = 8;
const TOOLTIP_OFFSET = 14;

/**
 * Renders an overlay with a highlight ring around the current tour step's target element
 * and a floating tooltip card. Re-positions on scroll / resize / DOM mutations.
 */
export default function TourOverlay() {
  const { active, step, stepIdx, total, stop, next, prev } = useTour();
  const location = useLocation();
  const [rect, setRect] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const targetElRef = useRef(null);

  // Resolve the target element (poll for up to 2 seconds — components mount async on route change)
  useEffect(() => {
    if (!active || !step) { setRect(null); setNotFound(false); return; }
    // Wait until the URL actually matches the step's matchRoute (if any)
    if (step.matchRoute && !step.matchRoute.test(location.pathname)) {
      setRect(null);
      setNotFound(false);
      return;
    }
    setNotFound(false);
    let attempts = 0;
    let cancelled = false;

    const find = () => {
      if (cancelled) return;
      // For selectors with comma (multi-target), pick the first visible one
      const candidates = document.querySelectorAll(step.target);
      let el = null;
      for (const c of candidates) {
        const r = c.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) { el = c; break; }
      }
      if (el) {
        targetElRef.current = el;
        updateRect(el);
        // Smooth scroll into view if off-screen
        const r = el.getBoundingClientRect();
        if (r.top < 60 || r.bottom > window.innerHeight - 120) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          // Re-measure after scroll
          setTimeout(() => { if (targetElRef.current) updateRect(targetElRef.current); }, 350);
        }
      } else if (attempts < 25) {
        attempts += 1;
        setTimeout(find, 120);
      } else {
        setNotFound(true);
      }
    };

    find();
    return () => { cancelled = true; };
  }, [active, step, location.pathname]);

  // Reposition on scroll / resize
  useLayoutEffect(() => {
    if (!active || !targetElRef.current) return;
    const handler = () => {
      if (targetElRef.current) updateRect(targetElRef.current);
    };
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    const interval = setInterval(handler, 500); // catch dom changes
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
      clearInterval(interval);
    };
  }, [active, step]);

  const updateRect = (el) => {
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - PADDING,
      left: r.left - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
    });
  };

  if (!active || !step) return null;

  // Tooltip placement
  let tooltipStyle = { top: 0, left: 0 };
  if (rect) {
    const placement = step.placement || "bottom";
    const tooltipW = 360;
    const tooltipH = 200; // estimate
    if (placement === "bottom" || placement === "bottom-end") {
      tooltipStyle.top = rect.top + rect.height + TOOLTIP_OFFSET;
      tooltipStyle.left = placement === "bottom-end"
        ? Math.max(8, rect.left + rect.width - tooltipW)
        : Math.max(8, rect.left + rect.width / 2 - tooltipW / 2);
    } else if (placement === "top") {
      tooltipStyle.top = Math.max(8, rect.top - tooltipH - TOOLTIP_OFFSET);
      tooltipStyle.left = Math.max(8, rect.left + rect.width / 2 - tooltipW / 2);
    } else if (placement === "right") {
      tooltipStyle.top = Math.max(8, rect.top);
      tooltipStyle.left = Math.min(window.innerWidth - tooltipW - 8, rect.left + rect.width + TOOLTIP_OFFSET);
    } else if (placement === "left") {
      tooltipStyle.top = Math.max(8, rect.top);
      tooltipStyle.left = Math.max(8, rect.left - tooltipW - TOOLTIP_OFFSET);
    }
    // Clamp to viewport
    tooltipStyle.left = Math.min(tooltipStyle.left, window.innerWidth - tooltipW - 8);
    tooltipStyle.top = Math.min(Math.max(8, tooltipStyle.top), window.innerHeight - tooltipH - 8);
  }

  return (
    <div className="fixed inset-0 z-[1000] pointer-events-none" data-testid="tour-overlay">
      {/* Dim layer with cut-out */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "auto" }} onClick={(e) => e.stopPropagation()}>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx="8"
                ry="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(15, 23, 42, 0.55)" mask="url(#tour-mask)" />
      </svg>

      {/* Highlight ring with pulse */}
      {rect && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: rect.top, left: rect.left, width: rect.width, height: rect.height,
          }}
        >
          <div className="absolute inset-0 rounded-md ring-2 ring-[#F05D23] tour-pulse" />
        </div>
      )}

      {/* Tooltip */}
      <div
        className="absolute w-[360px] bg-white border border-slate-200 rounded-md shadow-2xl pointer-events-auto"
        style={tooltipStyle}
        data-testid="tour-tooltip"
      >
        <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-wider text-[#F05D23] font-bold flex items-center gap-1">
              <Compass className="w-3 h-3" /> Live Tour · {stepIdx + 1} of {total}
            </div>
            <h4 className="font-semibold text-slate-900 text-sm mt-1">{step.title}</h4>
          </div>
          <button onClick={stop} className="text-slate-400 hover:text-slate-700 flex-shrink-0" data-testid="tour-close-btn" aria-label="End tour">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-3">
          <p className="text-[13px] text-slate-700 leading-relaxed">{step.body}</p>
          {notFound && (
            <div className="mt-3 text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              We couldn&apos;t spot this element on the current screen — you may have skipped ahead. Click <strong>Next</strong> to continue.
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-b-md">
          <div className="flex items-center gap-1">
            {TOUR_STEPS.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${i === stepIdx ? "bg-[#F05D23]" : i < stepIdx ? "bg-[#0A4C95]" : "bg-slate-300"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="ghost" onClick={prev} disabled={stepIdx === 0} data-testid="tour-prev-btn">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
            </Button>
            <Button size="sm" onClick={next} className="bg-[#F05D23] hover:bg-[#d94e18]" data-testid="tour-next-btn">
              {stepIdx === total - 1 ? "Finish" : (<>Next <ArrowRight className="w-3.5 h-3.5 ml-1" /></>)}
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tourPulse {
          0%   { box-shadow: 0 0 0 0 rgba(240, 93, 35, 0.55); }
          70%  { box-shadow: 0 0 0 14px rgba(240, 93, 35, 0); }
          100% { box-shadow: 0 0 0 0 rgba(240, 93, 35, 0); }
        }
        .tour-pulse { animation: tourPulse 1.6s ease-out infinite; }
      `}</style>
    </div>
  );
}
